import { useCallback, useContext, useState } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import CleanupLayout from '../Cleanup/CleanupLayout';
// Page text color: CleanupLayout applies viewRootTextPrimarySx (util/chartTheme).
import CleanupActionButtons from '../Cleanup/CleanupActionButtons';
import CapacityCacheDayContent from './CapacityCacheDayContent';
import useCapacityCache from '../../hooks/useCapacityCache';

export default function CapacityCacheCleanup() {
  const authCtx = useContext(AuthContext);
  const {
    clearAllCapacityCache,
    clearDayCapacityCache,
    clearEntertainmentCapacityCache,
    clearDocumentCache,
    regenerateCapacityCache,
    getDayCapacityCache,
  } = useCapacityCache();

  const canClearAll = authCtx.checkRight('internalBooking:special:clearAllCache');
  const canClearDay = authCtx.checkRight('internalBooking:special:clearDayCache');
  const canClearEntertainment = authCtx.checkRight(
    'internalBooking:special:clearDayCacheEntertainment'
  );
  const canClearDocumentCache = authCtx.checkRight('internalBooking:special:clearDocumentCache');

  const [isClearingAll, setIsClearingAll] = useState(false);
  const [isClearingDocumentCache, setIsClearingDocumentCache] = useState(false);
  const [isClearingAllWithDocumentCache, setIsClearingAllWithDocumentCache] = useState(false);
  const [isClearingDay, setIsClearingDay] = useState(false);
  const [isRebuildingDay, setIsRebuildingDay] = useState(false);
  const [isRebuildingMonth, setIsRebuildingMonth] = useState(false);
  const [isClearingEntertainment, setIsClearingEntertainment] = useState(false);
  const [lastClearAllResult, setLastClearAllResult] = useState(null);
  const [lastDayActionResult, setLastDayActionResult] = useState(null);
  const [lastClearEntertainmentResult, setLastClearEntertainmentResult] = useState(null);

  const performClearAll = useCallback(async () => {
    setIsClearingAll(true);
    try {
      const response = await clearAllCapacityCache();
      setLastClearAllResult(response);
      toast.success('Kapazitäts-Cache vollständig geleert.');
    } catch (error) {
      toast.error('Fehler beim Leeren des Kapazitäts-Caches.');
    } finally {
      setIsClearingAll(false);
    }
  }, [clearAllCapacityCache]);

  const performClearDocumentCache = useCallback(async () => {
    setIsClearingDocumentCache(true);
    try {
      await clearDocumentCache();
      toast.success('Dokument-Cache vollständig geleert.');
    } catch (error) {
      toast.error('Fehler beim Leeren des Dokument-Caches.');
    } finally {
      setIsClearingDocumentCache(false);
    }
  }, [clearDocumentCache]);

  const performClearAllWithDocumentCache = useCallback(async () => {
    setIsClearingAllWithDocumentCache(true);
    setIsClearingAll(true);
    setIsClearingDocumentCache(true);
    try {
      const clearAllResponse = await clearAllCapacityCache();
      setLastClearAllResult(clearAllResponse);
      await clearDocumentCache();
      toast.success('Kapazitäts-Cache und Dokument-Cache vollständig geleert.');
    } catch (error) {
      toast.error('Fehler beim Leeren von Kapazitäts-Cache und Dokument-Cache.');
    } finally {
      setIsClearingAll(false);
      setIsClearingDocumentCache(false);
      setIsClearingAllWithDocumentCache(false);
    }
  }, [clearAllCapacityCache, clearDocumentCache]);

  const performClearDay = useCallback(
    async selection => {
      if (!selection?.date || !selection?.entertainmentId) {
        toast.error('Bitte wähle zuerst Entertainment und Datum aus.');
        return;
      }
      setIsClearingDay(true);
      try {
        const response = await clearDayCapacityCache({
          entertainmentId: selection.entertainmentId,
          date: selection.date,
          rebuildCache: false,
        });
        setLastDayActionResult({
          selection,
          response,
          meta: {
            action: 'clear-day',
            rebuild: false,
          },
        });
        toast.success(
          `Cache für ${selection.entertainmentName || 'dieses Entertainment'} am ${dayjs(
            selection.date
          ).format('DD.MM.YYYY')} geleert (kein Neuaufbau gestartet).`
        );
      } catch (error) {
        toast.error('Fehler beim Leeren des Tages-Caches.');
      } finally {
        setIsClearingDay(false);
      }
    },
    [clearDayCapacityCache]
  );

  const performRebuildDay = useCallback(
    async selection => {
      if (!selection?.date || !selection?.entertainmentId) {
        toast.error('Bitte wähle zuerst Entertainment und Datum aus.');
        return;
      }
      setIsRebuildingDay(true);
      try {
        const response = await clearDayCapacityCache({
          entertainmentId: selection.entertainmentId,
          date: selection.date,
          rebuildCache: true,
        });
        setLastDayActionResult({
          selection,
          response,
          meta: {
            action: 'rebuild-day',
            rebuild: true,
          },
        });
        toast.success(
          `Cache für ${selection.entertainmentName || 'dieses Entertainment'} am ${dayjs(
            selection.date
          ).format('DD.MM.YYYY')} neu aufgebaut.`
        );
      } catch (error) {
        toast.error('Fehler beim Neuaufbau des Tages-Caches.');
      } finally {
        setIsRebuildingDay(false);
      }
    },
    [clearDayCapacityCache]
  );

  const performRebuildMonth = useCallback(
    async selection => {
      if (!selection?.date || !selection?.entertainmentId) {
        toast.error('Bitte wähle zuerst Entertainment und Datum aus.');
        return;
      }
      const targetDate = dayjs(selection.date);
      const startDate = targetDate.startOf('month').format('YYYY-MM-DD');
      const endDate = targetDate.endOf('month').format('YYYY-MM-DD');
      const monthLabel = targetDate.format('MM.YYYY');

      setIsRebuildingMonth(true);
      try {
        const response = await regenerateCapacityCache({
          entertainmentId: selection.entertainmentId,
          startDate,
          endDate,
          rebuildCache: true,
        });
        setLastDayActionResult({
          selection,
          response,
          meta: {
            action: 'rebuild-month',
            rebuild: true,
            startDate,
            endDate,
            monthLabel,
          },
        });
        toast.success(
          `Cache für ${selection.entertainmentName || 'dieses Entertainment'} im Monat ${monthLabel} neu aufgebaut.`
        );
      } catch (error) {
        toast.error('Fehler beim Neuaufbau des Monats-Caches.');
      } finally {
        setIsRebuildingMonth(false);
      }
    },
    [regenerateCapacityCache]
  );

  const performClearEntertainment = useCallback(
    async selection => {
      if (!selection?.entertainmentId) {
        toast.error('Bitte wähle zuerst ein Entertainment aus.');
        return;
      }
      setIsClearingEntertainment(true);
      try {
        const response = await clearEntertainmentCapacityCache({
          entertainmentId: selection.entertainmentId,
        });
        setLastClearEntertainmentResult({ selection, response });
        toast.success(
          `Cache für ${selection.entertainmentName || 'dieses Entertainment'} vollständig geleert.`
        );
      } catch (error) {
        toast.error('Fehler beim Leeren des Entertainment-Caches.');
      } finally {
        setIsClearingEntertainment(false);
      }
    },
    [clearEntertainmentCapacityCache]
  );

  const renderActions = useCallback(
    ({ selectedDate, selectedEntertainmentId, selectedEntertainmentName }) => {
      const dateLabel = selectedDate ? dayjs(selectedDate).format('DD.MM.YYYY') : null;
      const actions = [];

      if (canClearEntertainment) {
        actions.push({
          key: 'clear-entertainment',
          label: isClearingEntertainment
            ? 'Entertainment-Cache wird geleert…'
            : 'Entertainment-Cache leeren',
          disabled: isClearingEntertainment || !selectedEntertainmentId,
          onExecute: () =>
            performClearEntertainment({
              entertainmentId: selectedEntertainmentId,
              entertainmentName: selectedEntertainmentName,
            }),
          confirm: {
            title: 'Entertainment-Cache leeren?',
            description: selectedEntertainmentName
              ? `Möchtest du den Kapazitäts-Cache für ${selectedEntertainmentName} im gesamten Zeitraum löschen?`
              : 'Bitte wähle zuerst ein Entertainment aus.',
            confirmLabel: 'Leeren',
          },
        });
      }

      if (canClearDay) {
        actions.push({
          key: 'clear-day',
          label: isClearingDay ? 'Wird geleert…' : 'Tages-Cache leeren',
          disabled: isClearingDay || !selectedEntertainmentId || !selectedDate,
          onExecute: () =>
            performClearDay({
              entertainmentId: selectedEntertainmentId,
              entertainmentName: selectedEntertainmentName,
              date: selectedDate,
            }),
          confirm: {
            title: 'Tages-Cache leeren?',
            description: selectedDate
              ? `Möchtest du den Kapazitäts-Cache für ${
                  selectedEntertainmentName || 'dieses Entertainment'
                } am ${dateLabel} leeren, ohne einen Neuaufbau zu starten?`
              : 'Bitte wähle zuerst Entertainment und Datum aus.',
            confirmLabel: 'Leeren',
          },
        });

        actions.push({
          key: 'rebuild-day',
          label: isRebuildingDay ? 'Neuaufbau läuft…' : 'Cache für Tag neu bauen',
          disabled: isRebuildingDay || !selectedEntertainmentId || !selectedDate,
          color: 'success',
          onExecute: () =>
            performRebuildDay({
              entertainmentId: selectedEntertainmentId,
              entertainmentName: selectedEntertainmentName,
              date: selectedDate,
            }),
          confirm: {
            title: 'Cache für Tag neu bauen?',
            description: selectedDate
              ? `Möchtest du den Kapazitäts-Cache für ${
                  selectedEntertainmentName || 'dieses Entertainment'
                } am ${dateLabel} neu aufbauen lassen?`
              : 'Bitte wähle zuerst Entertainment und Datum aus.',
            confirmLabel: 'Neu bauen',
          },
        });

        actions.push({
          key: 'rebuild-month',
          label: isRebuildingMonth ? 'Monats-Neuaufbau läuft…' : 'Cache für Monat neu bauen',
          disabled: isRebuildingMonth || !selectedEntertainmentId || !selectedDate,
          color: 'success',
          onExecute: () =>
            performRebuildMonth({
              entertainmentId: selectedEntertainmentId,
              entertainmentName: selectedEntertainmentName,
              date: selectedDate,
            }),
          confirm: {
            title: 'Cache für Monat neu bauen?',
            description: selectedDate
              ? `Möchtest du den Kapazitäts-Cache für ${
                  selectedEntertainmentName || 'dieses Entertainment'
                } im ausgewählten Monat neu aufbauen lassen?`
              : 'Bitte wähle zuerst Entertainment und Datum aus.',
            confirmLabel: 'Neu bauen',
          },
        });
      }

      if (canClearAll) {
        actions.push({
          key: 'clear-all',
          label: isClearingAll ? 'Wird geleert…' : 'Gesamten Cache leeren',
          disabled: isClearingAll,
          onExecute: performClearAll,
          confirm: {
            title: 'Gesamten Kapazitäts-Cache leeren?',
            description: 'Diese Aktion löscht alle Kapazitäts-Caches. Möchtest du wirklich fortfahren?',
            confirmLabel: 'Leeren',
          },
        });
      }

      if (canClearDocumentCache) {
        actions.push({
          key: 'clear-document-cache',
          label: isClearingDocumentCache ? 'Wird geleert…' : 'Dokument-Cache leeren',
          disabled: isClearingDocumentCache,
          color: 'warning',
          onExecute: performClearDocumentCache,
          confirm: {
            title: 'Dokument-Cache leeren?',
            description:
              'Diese Aktion löscht interne Dokument-Caches (z. B. Stammdaten-/Modell-Caches). Möchtest du wirklich fortfahren?',
            confirmLabel: 'Leeren',
          },
        });
      }

      if (canClearAll && canClearDocumentCache) {
        actions.push({
          key: 'clear-all-with-document-cache',
          label: isClearingAllWithDocumentCache
            ? 'Beide Caches werden geleert…'
            : 'Gesamten Cache + Dokument-Cache leeren',
          disabled:
            isClearingAllWithDocumentCache || isClearingAll || isClearingDocumentCache,
          color: 'error',
          onExecute: performClearAllWithDocumentCache,
          confirm: {
            title: 'Gesamten Cache und Dokument-Cache leeren?',
            description:
              'Diese Aktion löscht alle Kapazitäts-Caches und zusätzlich alle internen Dokument-Caches. Möchtest du wirklich fortfahren?',
            confirmLabel: 'Alles leeren',
          },
        });
      }

      if (actions.length === 0) {
        return null;
      }

      return <CleanupActionButtons actions={actions} />;
    },
    [
      canClearAll,
      canClearDay,
      canClearEntertainment,
      canClearDocumentCache,
      isClearingAll,
      isClearingAllWithDocumentCache,
      isClearingDocumentCache,
      isClearingDay,
      isRebuildingDay,
      isRebuildingMonth,
      isClearingEntertainment,
      performClearAll,
      performClearAllWithDocumentCache,
      performClearDocumentCache,
      performClearDay,
      performRebuildDay,
      performRebuildMonth,
      performClearEntertainment,
    ]
  );

  const renderDayContent = useCallback(
    ({ selectedEntertainmentId, selectedDate }) => (
      <CapacityCacheDayContent
        selectedEntertainmentId={selectedEntertainmentId}
        selectedDate={selectedDate}
        fetchDayCache={getDayCapacityCache}
        lastClearAllResult={lastClearAllResult}
        lastDayActionResult={lastDayActionResult}
        lastClearEntertainmentResult={lastClearEntertainmentResult}
      />
    ),
    [getDayCapacityCache, lastClearAllResult, lastDayActionResult, lastClearEntertainmentResult]
  );

  return (
    <CleanupLayout
      requiredRightsAny={[
        'internalBooking:special:clearAllCache',
        'internalBooking:special:clearDayCache',
        'internalBooking:special:clearDayCacheEntertainment',
        'internalBooking:special:clearDocumentCache',
      ]}
      renderActions={renderActions}
      renderDayContent={renderDayContent}
    />
  );
}

